import { Typography } from '@execonline-inc/execonline-ui';
import {
  AcademicCapIcon,
  BookOpenIcon,
  UserGroupIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { assertNever } from '@kofno/piper';
import { ComponentType, SVGProps } from 'react';
import { T } from '../../../../../Translations';
import {
  ALL_PROGRAM_CATEGORIES,
  ProgramCategory,
  programCategoryFor,
  programCategoryLabel,
} from '../../../../Licensing/ProgramCategories';
import { ProgramFamily } from './LicenseAlertsStore/Types';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

const iconFor = (category: ProgramCategory): HeroIcon => {
  switch (category) {
    case 'professor-led':
      return AcademicCapIcon;
    case 'on-demand':
      return BookOpenIcon;
    case 'leadership-coaching':
      return UserGroupIcon;
    case 'group-coaching':
      return UsersIcon;
    default:
      return assertNever(category);
  }
};

interface Props {
  programFamilies: ReadonlyArray<ProgramFamily>;
}

function CollectionSummary({ programFamilies }: Props) {
  // A category the collection does not contain is not worth a slot.
  const present = ALL_PROGRAM_CATEGORIES.map((category) => ({
    category,
    count: programFamilies.filter(
      (programFamily) => programCategoryFor(programFamily.offeringType) === category,
    ).length,
  })).filter((entry) => entry.count > 0);

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border pb-4"
      data-intercom-target="share-license-alert-collection-summary"
    >
      <Typography
        variant="caption"
        fontWeight="medium"
        className="tracking-wide text-muted-foreground"
      >
        <T kind="In this collection" />
      </Typography>
      {present.map(({ category, count }) => {
        const CategoryIcon = iconFor(category);

        return (
          <div
            key={category}
            className="flex items-center gap-2"
            data-intercom-target={`share-license-alert-category-${category}`}
          >
            <CategoryIcon className="h-4 w-4 flex-none text-primary" aria-hidden="true" />
            <Typography variant="subtitleSmall" as="span" fontWeight="semibold">
              {count}
            </Typography>
            <Typography variant="bodyMedium" className="text-muted-foreground">
              <T kind={programCategoryLabel(category)} />
            </Typography>
          </div>
        );
      })}
    </div>
  );
}

export default CollectionSummary;
